import type { CourseSearchRecord, CourseSummary, ProfessorIndexRecord } from "../../shared/types";
import { toCourseSearchRecord } from "../../shared/static-artifacts";

function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function compactSearchText(value: string): string {
  return normalizeSearchText(value).replace(/\s+/g, "");
}

const PROFESSOR_NAME_VARIANTS = [
  ["alex", "alexander", "alexandra", "alexandre"],
  ["andrew", "drew"],
  ["benjamin", "ben"],
  ["charles", "charlie", "chuck"],
  ["christopher", "chris"],
  ["daniel", "dan", "danny"],
  ["douglas", "doug"],
  ["elizabeth", "liz", "lizzy", "beth"],
  ["jennifer", "jen", "jenny"],
  ["jonathan", "jon"],
  ["joseph", "joe"],
  ["katherine", "kate", "kathy", "katie"],
  ["matthew", "matt"],
  ["michael", "mike"],
  ["nicholas", "nick"],
  ["patricia", "pat", "patti"],
  ["rebecca", "becca"],
  ["robert", "rob", "bob"],
  ["samantha", "sam"],
  ["stephen", "steve"],
  ["thomas", "tom"],
  ["william", "will", "bill"],
] as const;

const PROFESSOR_NAME_VARIANT_LOOKUP = new Map<string, readonly string[]>(
  PROFESSOR_NAME_VARIANTS.flatMap((group) => group.map((name) => [name, group] as const)),
);

interface SearchQuery {
  normalized: string;
  compact: string;
  hasDigits: boolean;
}

interface PreparedSearchValue {
  normalized: string;
  compact: string;
}

function buildProfessorAliases(name: string): string[] {
  const trimmed = name.trim();
  if (!trimmed) {
    return [];
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length < 2) {
    return [];
  }

  const first = parts[0].replace(/\./g, "");
  const last = parts.at(-1) ?? "";
  if (!first || !last) {
    return [];
  }

  const initial = first.charAt(0).toUpperCase();
  const firstNameVariants = PROFESSOR_NAME_VARIANT_LOOKUP.get(first.toLowerCase()) ?? [first.toLowerCase()];
  return [
    `${initial}. ${last}`,
    `${initial} ${last}`,
    `${initial}.${last}`,
    ...firstNameVariants
      .filter((variant) => variant !== first.toLowerCase())
      .map((variant) => `${variant} ${last}`),
  ];
}

export interface PreparedCourseSearchEntry {
  course: CourseSearchRecord;
  identifierValues: PreparedSearchValue[];
  titleValues: PreparedSearchValue[];
  distribValues: PreparedSearchValue[];
}

export interface PreparedProfessorSearchEntry {
  professor: ProfessorIndexRecord;
  departmentSet: Set<string>;
  nameValues: PreparedSearchValue[];
  departmentValues: PreparedSearchValue[];
}

interface SearchResult<T> {
  item: T;
  score: number;
}

export interface RankedSearchResults<T> {
  results: T[];
  totalMatches: number;
}

function parseSearchQuery(query: string): SearchQuery {
  const normalized = normalizeSearchText(query);
  const compact = normalized.replace(/\s+/g, "");
  return {
    normalized,
    compact,
    hasDigits: /\d/.test(compact),
  };
}

function prepareSearchValue(value: string): PreparedSearchValue {
  return {
    normalized: normalizeSearchText(value),
    compact: compactSearchText(value),
  };
}

function hasTokenPrefixMatch(value: string, query: string): boolean {
  const valueTokens = value.split(" ").filter(Boolean);
  const queryTokens = query.split(" ").filter(Boolean);
  const usedValueIndexes = new Set<number>();

  return queryTokens.length > 0 && queryTokens.every((queryToken) => {
    const matchingIndex = valueTokens.findIndex(
      (valueToken, index) => !usedValueIndexes.has(index) && valueToken.startsWith(queryToken),
    );
    if (matchingIndex === -1) {
      return false;
    }

    usedValueIndexes.add(matchingIndex);
    return true;
  });
}

function isWithinOneEdit(value: string, query: string): boolean {
  if (value === query || Math.abs(value.length - query.length) > 1) {
    return value === query;
  }

  if (value.length === query.length) {
    let differences = 0;
    for (let index = 0; index < value.length; index += 1) {
      if (value[index] !== query[index]) {
        differences += 1;
        if (differences > 1) {
          return false;
        }
      }
    }
    return true;
  }

  const longer = value.length > query.length ? value : query;
  const shorter = value.length > query.length ? query : value;
  let longerIndex = 0;
  let shorterIndex = 0;
  let differences = 0;

  while (longerIndex < longer.length && shorterIndex < shorter.length) {
    if (longer[longerIndex] === shorter[shorterIndex]) {
      longerIndex += 1;
      shorterIndex += 1;
      continue;
    }

    differences += 1;
    if (differences > 1) {
      return false;
    }
    longerIndex += 1;
  }

  return true;
}

function hasFuzzyTokenMatch(value: string, query: string): boolean {
  const valueTokens = value.split(" ").filter(Boolean);
  const queryTokens = query.split(" ").filter(Boolean);
  const usedValueIndexes = new Set<number>();

  return queryTokens.length > 0 && queryTokens.every((queryToken) => {
    if (queryToken.length < 4) {
      return false;
    }

    const matchingIndex = valueTokens.findIndex(
      (valueToken, index) => (
        !usedValueIndexes.has(index)
        && isWithinOneEdit(valueToken, queryToken)
      ),
    );
    if (matchingIndex === -1) {
      return false;
    }

    usedValueIndexes.add(matchingIndex);
    return true;
  });
}

function uniquePreparedValues(values: string[]): PreparedSearchValue[] {
  const seen = new Set<string>();
  const prepared: PreparedSearchValue[] = [];

  for (const value of values) {
    if (!value) {
      continue;
    }

    const nextValue = prepareSearchValue(value);
    if (!nextValue.normalized) {
      continue;
    }

    const key = `${nextValue.normalized}__${nextValue.compact}`;
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    prepared.push(nextValue);
  }

  return prepared;
}

function parseShortName(shortName: string): {
  department: string;
  paddedNumber: string;
  numericNumber: string;
  subnumber: string | null;
} | null {
  const match = shortName.match(/^([A-Z]{2,8})(\d{3})(?:\.(\d{2}))?$/);
  if (!match) {
    return null;
  }

  const [, department, paddedNumber, subnumber] = match;
  return {
    department,
    paddedNumber,
    numericNumber: String(Number(paddedNumber)),
    subnumber: subnumber ?? null,
  };
}

function buildCourseAliases(course: CourseSearchRecord): string[] {
  const parsed = parseShortName(course.shortName);
  if (!parsed) {
    return [];
  }

  const baseAliases = [
    `${parsed.department} ${parsed.paddedNumber}`,
    `${parsed.department}${parsed.paddedNumber}`,
    `${parsed.department} ${parsed.numericNumber}`,
    `${parsed.department}${parsed.numericNumber}`,
    `${course.departmentName} ${parsed.paddedNumber}`,
    `${course.departmentName}${parsed.paddedNumber}`,
    `${course.departmentName} ${parsed.numericNumber}`,
    `${course.departmentName}${parsed.numericNumber}`,
  ];

  if (!parsed.subnumber) {
    return baseAliases;
  }

  return [
    ...baseAliases,
    `${parsed.department} ${parsed.numericNumber}.${parsed.subnumber}`,
    `${parsed.department}${parsed.numericNumber}.${parsed.subnumber}`,
    `${course.departmentName} ${parsed.numericNumber}.${parsed.subnumber}`,
    `${course.departmentName}${parsed.numericNumber}.${parsed.subnumber}`,
  ];
}

function scorePreparedValue(value: PreparedSearchValue, query: SearchQuery, allowFuzzy = true): number {
  if (!query.normalized) {
    return 0;
  }

  if (value.normalized === query.normalized) {
    return 120;
  }
  if (value.compact === query.compact) {
    return 110;
  }
  if (query.hasDigits) {
    return 0;
  }
  if (value.normalized.startsWith(query.normalized)) {
    return 80;
  }
  if (value.compact.startsWith(query.compact)) {
    return 72;
  }
  if (hasTokenPrefixMatch(value.normalized, query.normalized)) {
    return 64;
  }
  if (value.normalized.includes(query.normalized)) {
    return 48;
  }
  if (value.compact.includes(query.compact)) {
    return 42;
  }
  if (allowFuzzy && hasFuzzyTokenMatch(value.normalized, query.normalized)) {
    return 24;
  }

  return 0;
}

function bestPreparedScore(values: PreparedSearchValue[], query: SearchQuery, allowFuzzy = true): number {
  return values.reduce((highest, value) => Math.max(highest, scorePreparedValue(value, query, allowFuzzy)), 0);
}

function compareCourseResults(left: SearchResult<CourseSearchRecord>, right: SearchResult<CourseSearchRecord>): number {
  if (right.score !== left.score) {
    return right.score - left.score;
  }
  if (right.item.legacyReviewCount !== left.item.legacyReviewCount) {
    return right.item.legacyReviewCount - left.item.legacyReviewCount;
  }
  return left.item.shortName.localeCompare(right.item.shortName);
}

function compareProfessorResults(
  left: SearchResult<ProfessorIndexRecord>,
  right: SearchResult<ProfessorIndexRecord>,
): number {
  if (right.score !== left.score) {
    return right.score - left.score;
  }
  if (right.item.reviewCount !== left.item.reviewCount) {
    return right.item.reviewCount - left.item.reviewCount;
  }
  return left.item.name.localeCompare(right.item.name);
}

function pushRankedResult<T>(
  ranked: SearchResult<T>[],
  nextResult: SearchResult<T>,
  limit: number,
  compare: (left: SearchResult<T>, right: SearchResult<T>) => number,
): void {
  let insertAt = ranked.findIndex((candidate) => compare(nextResult, candidate) < 0);
  if (insertAt === -1) {
    insertAt = ranked.length;
  }

  if (insertAt >= limit) {
    return;
  }

  ranked.splice(insertAt, 0, nextResult);
  if (ranked.length > limit) {
    ranked.length = limit;
  }
}

export function prepareCourseSearchEntries(courses: CourseSearchRecord[]): PreparedCourseSearchEntry[] {
  return courses.map((course) => ({
    course,
    identifierValues: uniquePreparedValues([
      course.shortName,
      course.department,
      course.departmentName,
      ...buildCourseAliases(course),
    ]),
    titleValues: uniquePreparedValues([course.title]),
    distribValues: uniquePreparedValues(course.distribs),
  }));
}

export function prepareProfessorSearchEntries(
  professors: ProfessorIndexRecord[],
): PreparedProfessorSearchEntry[] {
  return professors.map((professor) => ({
    professor,
    departmentSet: new Set(professor.departments),
    nameValues: uniquePreparedValues([professor.name, ...buildProfessorAliases(professor.name)]),
    departmentValues: uniquePreparedValues(professor.departments),
  }));
}

function scorePreparedCourse(entry: PreparedCourseSearchEntry, query: SearchQuery): number {
  const identifierScore = bestPreparedScore(entry.identifierValues, query, false);
  const titleScore = bestPreparedScore(entry.titleValues, query);
  const distribScore = bestPreparedScore(entry.distribValues, query, false);

  return (identifierScore * 4) + (titleScore * 2) + distribScore;
}

function scorePreparedProfessor(entry: PreparedProfessorSearchEntry, query: SearchQuery): number {
  const nameScore = bestPreparedScore(entry.nameValues, query);
  const departmentScore = bestPreparedScore(entry.departmentValues, query, false);
  return (nameScore * 4) + departmentScore;
}

export function searchCourseEntries(
  entries: PreparedCourseSearchEntry[],
  query: string,
  department: string,
  limit?: number,
): RankedSearchResults<CourseSearchRecord> {
  const parsedQuery = parseSearchQuery(query);
  const ranked: SearchResult<CourseSearchRecord>[] = [];
  let totalMatches = 0;
  const shouldLimit = Number.isFinite(limit) && typeof limit === "number";

  for (const entry of entries) {
    if (department && entry.course.department !== department) {
      continue;
    }

    const score = parsedQuery.normalized ? scorePreparedCourse(entry, parsedQuery) : 0;
    if (parsedQuery.normalized && score <= 0) {
      continue;
    }

    totalMatches += 1;

    if (shouldLimit) {
      pushRankedResult(ranked, { item: entry.course, score }, limit, compareCourseResults);
      continue;
    }

    ranked.push({ item: entry.course, score });
  }

  if (!shouldLimit) {
    ranked.sort(compareCourseResults);
  }

  return {
    results: ranked.map((entry) => entry.item),
    totalMatches,
  };
}

export function searchProfessorEntries(
  entries: PreparedProfessorSearchEntry[],
  query: string,
  department: string,
  limit?: number,
): RankedSearchResults<ProfessorIndexRecord> {
  const parsedQuery = parseSearchQuery(query);
  const ranked: SearchResult<ProfessorIndexRecord>[] = [];
  let totalMatches = 0;
  const shouldLimit = Number.isFinite(limit) && typeof limit === "number";

  for (const entry of entries) {
    if (department && !entry.departmentSet.has(department)) {
      continue;
    }

    const score = parsedQuery.normalized ? scorePreparedProfessor(entry, parsedQuery) : 0;
    if (parsedQuery.normalized && score <= 0) {
      continue;
    }

    totalMatches += 1;

    if (shouldLimit) {
      pushRankedResult(ranked, { item: entry.professor, score }, limit, compareProfessorResults);
      continue;
    }

    ranked.push({ item: entry.professor, score });
  }

  if (!shouldLimit) {
    ranked.sort(compareProfessorResults);
  }

  return {
    results: ranked.map((entry) => entry.item),
    totalMatches,
  };
}

export function scoreCourseQuery(course: CourseSummary | CourseSearchRecord, query: string): number {
  const searchRecord = "offeringPreview" in course ? course : toCourseSearchRecord(course);
  return scorePreparedCourse(prepareCourseSearchEntries([searchRecord])[0], parseSearchQuery(query));
}

export function matchesCourseQuery(course: CourseSummary | CourseSearchRecord, query: string): boolean {
  return scoreCourseQuery(course, query) > 0;
}

export function scoreProfessorQuery(course: CourseSummary | CourseSearchRecord, query: string): number {
  return bestPreparedScore(
    uniquePreparedValues((course.professorNames ?? []).flatMap((name) => [name, ...buildProfessorAliases(name)])),
    parseSearchQuery(query),
  );
}

export function matchesProfessorQuery(course: CourseSummary | CourseSearchRecord, query: string): boolean {
  const professorNames = course.professorNames ?? [];
  return professorNames.length === 0 ? !normalizeSearchText(query) : scoreProfessorQuery(course, query) > 0;
}

export function scoreProfessorRecord(professor: ProfessorIndexRecord, query: string): number {
  return scorePreparedProfessor(prepareProfessorSearchEntries([professor])[0], parseSearchQuery(query));
}

export function matchesProfessorRecord(professor: ProfessorIndexRecord, query: string): boolean {
  return scoreProfessorRecord(professor, query) > 0;
}
